import type { EventDefinition, ParamsType } from "../types";
/**
 * Event definitions for Snowplow
 * @type {EventDefinition[]}
 * @property {string} name - The name of the event, to use when triggering
 * @property {string} type - The type of the event (structured | unstructured)
 * @property {makePayload} makePayload
 *   - Function that creates the payload for the event;
 *   - Allows optional params object to be passed in
 *
 * @example
 * Parent
 * import { getSnowplowConfig, SnowplowProvider } from "@simplybusiness/services";
 * const snowplowProps = getSnowplowConfig(pageData);
 * <SnowplowProvider scripts={snowplowProps!}>{children}</SnowplowProvider>
 *
 * Child
 * import { useSnowplowContext } from "@simplybusiness/services";
 * const { snowplow } = useSnowplowContext();
 * const handlerFunction = () => snowplow?.trigger("eventNameHere");
 */

// Base page events
export const baseEventDefinitions: EventDefinition[] = [
  // Mobile link in header
  {
    name: "mobileLinkClick",
    type: "structured",
    makePayload: () => ({
      category: "marketing",
      action: "link-click",
      label: "mobile_call_button",
      property: window.location.href,
    }),
  },
  // Operating hours link in footer
  {
    name: "operatingHoursClick",
    type: "unstructured",
    makePayload: () => ({
      schema:
        "iglu:com.snowplowanalytics.snowplow/unstruct_event/jsonschema/1-0-0",
      data: {
        schema:
          "iglu:com.simplybusiness/operating_hours_clicked/jsonschema/1-0-2",
        data: {},
      },
    }),
  },
  {
    name: "progressbarClicked",
    type: "unstructured",
    makePayload: params => {
      const {
        section,
        context,
        fromStepIndex,
        fromStepName,
        stepIndex,
        stepName,
      } = params as ParamsType;

      return {
        schema: "iglu:com.simplybusiness/progressbar_clicked/jsonschema/1-1-0",
        data: {
          source_page_index: fromStepIndex,
          source_page_name: fromStepName,
          page_index: stepIndex,
          page_name: stepName,
          site: context.site,
          section_name: section,
          vertical: context.vertical,
        },
      };
    },
  },
  // Zendesk help chat link
  {
    name: "helpChatLinkClicked",
    type: "structured",
    makePayload: params => {
      const { label } = params as ParamsType;

      return {
        category: "help chat for question",
        action: "help_chat_for_question_clicked",
        label,
        property: window.location.href,
      };
    },
    contexts: ["serviceChannelContext"],
  },
];
